import React from 'react';
import { useSelector } from 'react-redux';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';

/**
 * Reusable Autocomplete component for selecting tags, variables, and local variables
 * Matches the style used in SCADA_Pages with RMMS color scheme
 */
const VariableAutocomplete = ({
  value,
  onChange,
  label,
  placeholder = "Search tags and variables...",
  fullWidth = true,
  size = "small",
  filterTypes = ['T', 'V', 'LV'], // Which types to show
  showConstants = false,
  ...otherProps
}) => {
  const allVariables = useSelector((state) => state.flow.allVariables) || [];

  // Filter and prepare variables
  const options = allVariables
    .filter(v => filterTypes.includes(v.type))
    .map(v => {
      let displayName;
      let id;
      if (v.type === 'T') {
        id = v.tagId !== undefined ? v.tagId : v.tagID;
        displayName = `${v.tagName || v.name} [${id}]`;
      } else if (v.type === 'V') {
        id = v.varId !== undefined ? v.varId : v.varID;
        displayName = `${v.varName || v.name} [${id}]`;
      } else {
        displayName = v.name || v.displayName;
      }
      return {
        ...v,
        displayName,
        description: v.description || (v.type === 'T' ? 'Tag' : v.type === 'V' ? 'Variable' : 'Local Variable')
      };
    });

  // Add constants if requested
  if (showConstants) {
    options.push(
      { id: 'const_true', name: 'true', displayName: 'true', type: 'constant', description: 'Boolean constant' },
      { id: 'const_false', name: 'false', displayName: 'false', type: 'constant', description: 'Boolean constant' },
      { id: 'const_null', name: 'null', displayName: 'null', type: 'constant', description: 'Null value' }
    );

    // Add standard datetime variables
    options.push(
      { id: 'Now.Year', name: 'Now.Year', displayName: 'Now.Year', type: 'datetime', description: 'Current year' },
      { id: 'Now.Month', name: 'Now.Month', displayName: 'Now.Month', type: 'datetime', description: 'Current month (1-12)' },
      { id: 'Now.Day', name: 'Now.Day', displayName: 'Now.Day', type: 'datetime', description: 'Current day (1-31)' },
      { id: 'Now.Hour', name: 'Now.Hour', displayName: 'Now.Hour', type: 'datetime', description: 'Current hour (0-23)' },
      { id: 'Now.Minute', name: 'Now.Minute', displayName: 'Now.Minute', type: 'datetime', description: 'Current minute (0-59)' },
      { id: 'Now.Second', name: 'Now.Second', displayName: 'Now.Second', type: 'datetime', description: 'Current second (0-59)' },
      { id: 'Now.DayOfWeek', name: 'Now.DayOfWeek', displayName: 'Now.DayOfWeek', type: 'datetime', description: 'Day of week (0=Sunday)' },
      { id: 'Now.DayOfYear', name: 'Now.DayOfYear', displayName: 'Now.DayOfYear', type: 'datetime', description: 'Day of year (1-366)' }
    );
  }

  const getBackgroundColor = (type) => {
    switch (type) {
      case 'T': return '#edbc45'; // RMMS primary (yellow/gold)
      case 'V': return '#fff3e0'; // Light orange
      case 'LV': return '#f3e5f5'; // Light purple
      case 'constant': return '#e0e0e0'; // Gray
      case 'datetime': return '#e8eaf6'; // Light indigo
      default: return '#f5f5f5';
    }
  };

  const getTextColor = (type) => {
    switch (type) {
      case 'T': return '#000'; // Black on yellow
      case 'V': return '#ef6c00'; // Orange
      case 'LV': return '#6a1b9a'; // RMMS accent (purple)
      case 'constant': return '#424242'; // Dark gray
      case 'datetime': return '#3f51b5'; // Indigo
      default: return '#000';
    }
  };

  const getDotColor = (type) => {
    switch (type) {
      case 'T': return '#2196f3'; // Blue
      case 'V': return '#ff9800'; // Orange
      case 'LV': return '#9c27b0'; // Purple
      case 'constant': return '#757575'; // Gray
      case 'datetime': return '#3f51b5'; // Indigo
      default: return '#bdbdbd';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'T': return 'TAG';
      case 'V': return 'VAR';
      case 'LV': return 'LOCAL';
      case 'constant': return 'CONST';
      case 'datetime': return 'TIME';
      default: return type;
    }
  };

  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      options={options}
      getOptionLabel={(option) => option.displayName || ''}
      value={value || null}
      onChange={onChange}
      ListboxProps={{
        style: {
          maxHeight: '200px'
        }
      }}
      sx={{
        width: '80%'
      }}
      slotProps={{
        popper: {
          sx: {
            width: '80%'
          }
        }
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: getDotColor(option.type),
              flexShrink: 0
            }}
          />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="body2" noWrap>
              {option.displayName}
            </Typography>
          </Box>
          <Box
            sx={{
              backgroundColor: getBackgroundColor(option.type),
              color: getTextColor(option.type),
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 'bold',
              flexShrink: 0
            }}
          >
            {getTypeLabel(option.type)}
          </Box>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
        />
      )}
      {...otherProps}
    />
  );
};

export default VariableAutocomplete;
